import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Divider,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as BackIcon,
  Campaign as CampaignIcon,
  DateRange as DateIcon,
  Percent as PercentIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
// import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
// import { es } from 'date-fns/locale';
import Layout from '../../components/Layout';
import apiService from '../../services/api';
import { 
  ComercioExtendido, 
  Campaña, 
  CampañaInput, 
  Local 
} from '../../types/comercio';

const ComercioCampañas: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const comercioId = parseInt(id || '0');

  // Estados principales
  const [comercio, setComercio] = useState<ComercioExtendido | null>(null);
  const [campañas, setCampañas] = useState<Campaña[]>([]);
  const [locales, setLocales] = useState<Local[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Estados del diálogo
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCampaña, setEditingCampaña] = useState<Campaña | null>(null);
  const [campanaForm, setCampanaForm] = useState<CampañaInput>({
    nombre: '',
    descripcion: '',
    fecha_inicio: new Date().toISOString().split('T')[0],
    fecha_fin: '',
    aplica_todo_negocio: true,
    locales_aplicables: [],
    consumo_minimo: 0,
    descuento_maximo: 0,
    porcentaje_descuento: 0,
    monto_descuento: 0,
    numero_promociones: 1
  });

  useEffect(() => {
    if (comercioId) {
      loadData();
    }
  }, [comercioId]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const [comercioResponse, campañasResponse] = await Promise.all([
        apiService.getComercio(comercioId),
        apiService.getComercio(comercioId) // campañas están anidadas en comercio.data.campañas
      ]);

      if (comercioResponse.success && comercioResponse.data) {
        setComercio(comercioResponse.data);
        setLocales(comercioResponse.data.locales || []);
        // Manejar campañas anidadas en comercio.data.campañas
        setCampañas(Array.isArray(comercioResponse.data.campañas) ? comercioResponse.data.campañas : []);
      } else {
        setComercio(null);
        setLocales([]);
        setCampañas([]);
      }
    } catch (error) {
      console.error('Error cargando datos:', error);
      setError('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (campaña?: Campaña) => {
    if (campaña) {
      setEditingCampaña(campaña);
      // Formatear fechas a YYYY-MM-DD si vienen como Date u objeto
      const formatDate = (f: any) => {
        if (!f) return '';
        if (typeof f === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(f)) return f;
        const d = new Date(f);
        return d.toISOString().split('T')[0];
      };
      setCampanaForm({
        nombre: campaña.nombre,
        descripcion: campaña.descripcion,
        fecha_inicio: formatDate(campaña.fecha_inicio),
        fecha_fin: formatDate(campaña.fecha_fin),
        aplica_todo_negocio: campaña.aplica_todo_negocio,
        locales_aplicables: campaña.locales_aplicables || [],
        consumo_minimo: parseFloat(campaña.consumo_minimo as any) || 0,
        descuento_maximo: parseFloat(campaña.descuento_maximo as any) || 0,
        porcentaje_descuento: parseFloat(campaña.porcentaje_descuento as any) || 0,
        monto_descuento: parseFloat(campaña.monto_descuento as any) || 0,
        numero_promociones: campaña.numero_promociones
      });
    } else {
      setEditingCampaña(null);
      setCampanaForm({
        nombre: '',
        descripcion: '',
        fecha_inicio: new Date().toISOString().split('T')[0],
        fecha_fin: '',
        aplica_todo_negocio: true,
        locales_aplicables: [],
        consumo_minimo: 0,
        descuento_maximo: 0,
        porcentaje_descuento: 5,
        monto_descuento: 0,
        numero_promociones: 1
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCampaña(null);
  };

  const handleSaveCampaña = async () => {
    setLoading(true);
    
    try {
      let response;
      if (editingCampaña) {
        // Actualizar campaña existente
        response = await apiService.updateCampaña(comercioId, editingCampaña.id, campanaForm);
      } else {
        // Crear nueva campaña
        response = await apiService.createCampaña(comercioId, campanaForm);
      }

      if (response.success) {
        handleCloseDialog();
        loadData(); // Recargar datos
      } else {
        setError(response.message || 'Error guardando campaña');
      }
    } catch (error) {
      console.error('Error guardando campaña:', error);
      setError('Error guardando campaña');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCampaña = async (campañaId: number) => {
    if (window.confirm('¿Está seguro de eliminar esta campaña? Esta acción no se puede deshacer.')) {
      setLoading(true);
      try {
        const response = await apiService.deleteCampaña(comercioId, campañaId);
        if (response.success) {
          loadData(); // Recargar datos
        } else {
          setError(response.message || 'Error eliminando campaña');
        }
      } catch (error) {
        console.error('Error eliminando campaña:', error);
        setError('Error eliminando campaña');
      } finally {
        setLoading(false);
      }
    }
  };

  const calcularDescuentoEjemplo = (): number => {
    const consumo = campanaForm.consumo_minimo || 50;
    let descuento = 0;

    if (campanaForm.porcentaje_descuento) {
      descuento = (consumo * campanaForm.porcentaje_descuento) / 100;
    } else if (campanaForm.monto_descuento) {
      descuento = campanaForm.monto_descuento;
    }

    if (campanaForm.descuento_maximo && descuento > campanaForm.descuento_maximo) {
      descuento = campanaForm.descuento_maximo;
    }

    return descuento;
  };

  const formatearFecha = (fecha: string): string => {
    return new Date(fecha).toLocaleDateString('es-PE');
  };

  const esCampañaVigente = (campaña: Campaña): boolean => {
    const hoy = new Date();
    const inicio = new Date(campaña.fecha_inicio);
    const fin = new Date(campaña.fecha_fin);
    return hoy >= inicio && hoy <= fin;
  };

  const formatearLocalesAplicables = (campaña: Campaña): string => {
    if (!campaña) return 'No registrada';
    if (campaña.aplica_todo_negocio) {
      return 'Todo el negocio';
    }
    if (!campaña.locales_aplicables || !Array.isArray(campaña.locales_aplicables) || campaña.locales_aplicables.length === 0) {
      return 'Sin locales específicos';
    }
    return `${campaña.locales_aplicables.length} local(es)`;
  };

  if (loading && !comercio) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!comercio) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Comercio no encontrado</Alert>
      </Box>
    );
  }

  return (
    <Layout>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/admin/comercios-ext')}
            sx={{ mr: 2 }}
          >
            Volver
          </Button>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" component="h1">
              Campañas de {comercio.razon_social}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              RUC: {comercio.ruc}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Nueva Campaña
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Estadísticas rápidas */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Paper sx={{ p: 2, flex: '1 1 200px' }}>
            <Typography variant="h6" color="primary">{campañas.length}</Typography>
            <Typography variant="body2">Total Campañas</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: '1 1 200px' }}>
            <Typography variant="h6" color="success.main">
              {campañas.filter(esCampañaVigente).length}
            </Typography>
            <Typography variant="body2">Vigentes</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: '1 1 200px' }}>
            <Typography variant="h6" color="info.main">
              {campañas.filter(c => c.aplica_todo_negocio).length}
            </Typography>
            <Typography variant="body2">Todo el Negocio</Typography>
          </Paper>
        </Box>

        {/* Tabla de campañas */}
        <Paper>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">
              Campañas ({campañas.length})
            </Typography>
          </Box>
          
          {campañas.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CampaignIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No hay campañas configuradas
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Crea campañas para ofrecer descuentos y promociones a tus clientes
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                Crear Primera Campaña
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Período</TableCell>
                    <TableCell>Descuento</TableCell>
                    <TableCell>Aplicabilidad</TableCell>
                    <TableCell align="center">Estado</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campañas.map((campaña) => (
                    <TableRow key={campaña.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {campaña.nombre}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {campaña.descripcion}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <DateIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {formatearFecha(campaña.fecha_inicio)} - {formatearFecha(campaña.fecha_fin)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          {campaña.porcentaje_descuento ? (
                            <Chip
                              icon={<PercentIcon />}
                              label={`${campaña.porcentaje_descuento}%`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ) : (
                            <Chip
                              icon={<MoneyIcon />}
                              label={`S/ ${campaña.monto_descuento}`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          )}
                          {campaña.descuento_maximo && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              Máx: S/ {campaña.descuento_maximo}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatearLocalesAplicables(campaña)}
                        </Typography>
                        {campaña.consumo_minimo && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Mín: S/ {campaña.consumo_minimo}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={esCampañaVigente(campaña) ? 'Vigente' : 'No vigente'}
                          color={esCampañaVigente(campaña) ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(campaña)}
                            title="Editar"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteCampaña(campaña.id)}
                            title="Eliminar"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Dialog para agregar/editar campaña */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingCampaña ? 'Editar Campaña' : 'Nueva Campaña'}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Información básica */}
              <Box>
                <Typography variant="h6" gutterBottom>Información Básica</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Nombre de la Campaña *"
                    value={campanaForm.nombre}
                    onChange={(e) => setCampanaForm({ ...campanaForm, nombre: e.target.value })}
                    placeholder="Ej: Descuento Mascotas365, Promoción Rústica, etc."
                  />
                  
                  <TextField
                    fullWidth
                    label="Descripción"
                    multiline
                    rows={2}
                    value={campanaForm.descripcion}
                    onChange={(e) => setCampanaForm({ ...campanaForm, descripcion: e.target.value })}
                    placeholder="Describe los términos y condiciones de la campaña"
                  />
                </Box>
              </Box>

              <Divider />

              {/* Período de vigencia */}
              <Box>
                <Typography variant="h6" gutterBottom>Período de Vigencia</Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    label="Fecha de Inicio *"
                    type="date"
                    value={campanaForm.fecha_inicio}
                    onChange={(e) => setCampanaForm({ 
                      ...campanaForm, 
                      fecha_inicio: e.target.value
                    })}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: '1 1 200px' }}
                  />
                  
                  <TextField
                    label="Fecha de Fin *"
                    type="date"
                    value={campanaForm.fecha_fin}
                    onChange={(e) => setCampanaForm({ 
                      ...campanaForm, 
                      fecha_fin: e.target.value
                    })}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: '1 1 200px' }}
                  />
                </Box>
              </Box>

              <Divider />

              {/* Configuración de descuento */}
              <Box>
                <Typography variant="h6" gutterBottom>Configuración de Descuento</Typography>
                
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <TextField
                    label="Porcentaje de Descuento"
                    type="number"
                    value={campanaForm.porcentaje_descuento || ''}
                    onChange={(e) => setCampanaForm({ 
                      ...campanaForm, 
                      porcentaje_descuento: parseFloat(e.target.value) || 0,
                      monto_descuento: 0 // Limpiar monto fijo
                    })}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    sx={{ flex: '1 1 200px' }}
                  />

                  <Typography sx={{ alignSelf: 'center', mx: 1 }}>O</Typography>

                  <TextField
                    label="Monto Fijo de Descuento"
                    type="number"
                    value={campanaForm.monto_descuento || ''}
                    onChange={(e) => setCampanaForm({ 
                      ...campanaForm, 
                      monto_descuento: parseFloat(e.target.value) || 0,
                      porcentaje_descuento: 0 // Limpiar porcentaje
                    })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">S/</InputAdornment>,
                    }}
                    sx={{ flex: '1 1 200px' }}
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    label="Consumo Mínimo"
                    type="number"
                    value={campanaForm.consumo_minimo || ''}
                    onChange={(e) => setCampanaForm({ 
                      ...campanaForm, 
                      consumo_minimo: parseFloat(e.target.value) || 0 
                    })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">S/</InputAdornment>,
                    }}
                    sx={{ flex: '1 1 200px' }}
                  />

                  <TextField
                    label="Descuento Máximo"
                    type="number"
                    value={campanaForm.descuento_maximo || ''}
                    onChange={(e) => setCampanaForm({ 
                      ...campanaForm, 
                      descuento_maximo: parseFloat(e.target.value) || 0 
                    })}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">S/</InputAdornment>,
                    }}
                    sx={{ flex: '1 1 200px' }}
                  />
                </Box>

                {/* Ejemplo de cálculo */}
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Ejemplo:</strong> Con un consumo de S/ {campanaForm.consumo_minimo || 50}, 
                    el descuento sería: <strong>S/ {calcularDescuentoEjemplo().toFixed(2)}</strong>
                  </Typography>
                </Alert>
              </Box>

              <Divider />

              {/* Aplicabilidad */}
              <Box>
                <Typography variant="h6" gutterBottom>Aplicabilidad</Typography>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={campanaForm.aplica_todo_negocio}
                      onChange={(e) => setCampanaForm({ 
                        ...campanaForm, 
                        aplica_todo_negocio: e.target.checked,
                        locales_aplicables: e.target.checked ? [] : campanaForm.locales_aplicables
                      })}
                    />
                  }
                  label="Aplicar a todo el negocio (incluyendo locales futuros)"
                  sx={{ mb: 2 }}
                />

                {!campanaForm.aplica_todo_negocio && locales.length > 0 && (
                  <FormControl fullWidth>
                    <InputLabel>Locales Específicos</InputLabel>
                    <Select
                      multiple
                      value={campanaForm.locales_aplicables || []}
                      onChange={(e) => setCampanaForm({ 
                        ...campanaForm, 
                        locales_aplicables: e.target.value as number[] 
                      })}
                      renderValue={(selected) => `${selected.length} local(es) seleccionado(s)`}
                    >
                      {locales.map((local) => (
                        <MenuItem key={local.id} value={local.id}>
                          <Checkbox checked={(campanaForm.locales_aplicables || []).includes(local.id)} />
                          <ListItemText primary={local.nombre} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <TextField
                  fullWidth
                  label="Número de Promociones"
                  type="number"
                  value={campanaForm.numero_promociones}
                  onChange={(e) => setCampanaForm({ 
                    ...campanaForm, 
                    numero_promociones: parseInt(e.target.value) || 1 
                  })}
                  helperText="¿Cuántas veces puede usar cada usuario esta promoción?"
                  sx={{ mt: 2 }}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button 
              variant="contained" 
              onClick={handleSaveCampaña}
              disabled={!campanaForm.nombre || !campanaForm.fecha_inicio || !campanaForm.fecha_fin}
            >
              {editingCampaña ? 'Actualizar' : 'Crear'} Campaña
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
};

export default ComercioCampañas;
